import { Injectable } from '@angular/core';
import { Apollo } from 'apollo-angular';
import { Observable, of } from 'rxjs';
import { catchError, map, switchMap, take } from 'rxjs/operators';
import { FeatureListService } from '../../../services/feature-list.service';
import { OrganizationDiffLog } from '../../../types/organization/organization-diff-log';
import {
  ActiveOrganization,
  Organization,
  RecentlyAddedOrganization,
} from '../../../types/user/organization';
import { ApolloInstances } from '../graphql.module';
import ACTIVE_ORGS from './queries/activeOrgs';
import GET_ORGANIZATION_NOTIFICATIONS from './queries/allOrganizationNotifications';
import GET_ORGANIZATION_PERMISSIONS from './queries/allOrganizationPermissions';
import GET_ORGANIZATION_SYSTEMS from './queries/allOrganizationSystems';
import GET_ORGANIZATION_USERS from './queries/allOrganizationUsers';
import GET_ALL_ORGANIZATIONS from './queries/allOrganizations';
import GET_ALL_ORGANIZATIONS_FOR_CONTEXT_NAME from './queries/allOrganizationsForContextName';
import GET_DIFF_LOG from './queries/diff-log';
import GET_RECENTLY_ADDED_ORGANIZATIONS from './queries/recentlyAddedOrganizations';
import GET_ALL_ORGANIZATION_PROVIDERS from './queries/get-all-organization-providers';
import ADD_ORGANIZATIONS_INTO_HIERARCHY from './mutations/add-organizations-into-hierarchy';
import REMOVE_ORGANIZATIONS_FROM_HIERARCHY from './mutations/remove-organizations-from-hierarchy';
import { noDefaultErrorHandler } from '../../../services/interceptors';

@Injectable({
  providedIn: 'root',
})
export class OrganizationGraphqlService {
  constructor(
    private apollo: Apollo,
    private featuresService: FeatureListService,
  ) {}

  getRecentlyAddedOrganizations(): Observable<
    Array<RecentlyAddedOrganization>
  > {
    return this.apollo
      .use(ApolloInstances.ORGANIZATION)
      .watchQuery<{
        recentlyAddedOrganizations: Array<RecentlyAddedOrganization>;
      }>({
        fetchPolicy: 'no-cache',
        query: GET_RECENTLY_ADDED_ORGANIZATIONS,
      })
      .valueChanges.pipe(
        take(1),
        map((request) =>
          request.data.recentlyAddedOrganizations.map((org) => {
            org.profileUrl =
              org.organizationType == 'Seller'
                ? `/aptitude/buyer/sellers/${org.id}`
                : `/aptitude/seller/SellerViewBuyerProfile?orgId=${org.id}`;
            return org;
          }),
        ),
      );
  }
  getAllOrganizations(): Observable<Array<Organization>> {
    return this.apollo
      .use(ApolloInstances.ORGANIZATION)
      .watchQuery<{ organizations: Array<Organization> }>({
        fetchPolicy: 'no-cache',
        query: GET_ALL_ORGANIZATIONS,
      })
      .valueChanges.pipe(
        take(1),
        map((request) => request.data.organizations),
      );
  }

  getAllBuyersForOrganization(): Observable<Array<Organization>> {
    const contextName = 'BUYERS_WITH_BA';
    return this.apollo
      .use(ApolloInstances.ORGANIZATION)
      .watchQuery<{ organizations: Array<Organization> }>({
        fetchPolicy: 'no-cache',
        variables: { contextName },
        query: GET_ALL_ORGANIZATIONS_FOR_CONTEXT_NAME,
      })
      .valueChanges.pipe(
        take(1),
        map((request) => request.data.organizations),
      );
  }

  getAllSellersForOrganization(): Observable<Array<Organization>> {
    const contextName = 'SELLERS_WITH_ACTIVE_AND_PENDING_SA';
    return this.apollo
      .use(ApolloInstances.ORGANIZATION)
      .watchQuery<{ organizations: Array<Organization> }>({
        fetchPolicy: 'no-cache',
        variables: { contextName },
        query: GET_ALL_ORGANIZATIONS_FOR_CONTEXT_NAME,
      })
      .valueChanges.pipe(
        take(1),
        map((request) => request.data.organizations),
      );
  }

  getOrganizationDiffLog(
    organizationId: number,
  ): Observable<OrganizationDiffLog[]> {
    return this.apollo
      .use(ApolloInstances.ORGANIZATION)
      .watchQuery<{ organizationDiffLog: OrganizationDiffLog[] }>({
        fetchPolicy: 'no-cache',
        variables: { organizationId },
        query: GET_DIFF_LOG,
      })
      .valueChanges.pipe(
        take(1),
        map((request) => request.data.organizationDiffLog),
      );
  }
  getOrganizationUsers(id: number): Observable<Organization> {
    return this.apollo
      .use(ApolloInstances.ORGANIZATION)
      .watchQuery<{ organization: Organization }>({
        fetchPolicy: 'no-cache',
        variables: { id },
        query: GET_ORGANIZATION_USERS,
      })
      .valueChanges.pipe(
        take(1),
        map((request) => request.data.organization),
      );
  }

  getOrganizationSystems(): Observable<Organization[]> {
    return this.featuresService.aptitudeFeatureList$.pipe(
      switchMap((features) =>
        this.apollo
          .use(ApolloInstances.ORGANIZATION)
          .watchQuery<{ organizations: Organization[] }>({
            fetchPolicy: 'no-cache',
            query: GET_ORGANIZATION_SYSTEMS(
              features.MANAGE_GPO_ORG_USERS_CAT_5699, // When retiring, remove the wrapping featuresService pipe and revert the query from a function to an assignment
            ),
          })
          .valueChanges.pipe(
            take(1),
            map((request) => request.data.organizations),
          ),
      ),
    );
  }

  getOrganizationPermissions(id: number): Observable<Organization> {
    return this.apollo
      .use(ApolloInstances.ORGANIZATION)
      .watchQuery<{ organization: Organization }>({
        fetchPolicy: 'no-cache',
        variables: { id },
        query: GET_ORGANIZATION_PERMISSIONS,
      })
      .valueChanges.pipe(
        take(1),
        map((request) => request.data.organization),
      );
  }

  getOrganizationNotifications(id: number): Observable<Organization> {
    return this.apollo
      .use(ApolloInstances.ORGANIZATION)
      .watchQuery<{ organization: Organization }>({
        fetchPolicy: 'no-cache',
        variables: { id },
        query: GET_ORGANIZATION_NOTIFICATIONS,
      })
      .valueChanges.pipe(
        take(1),
        map((request) => request.data.organization),
      );
  }

  getActiveOrganizations(
    isSeller: boolean,
  ): Observable<Array<ActiveOrganization>> {
    return this.apollo
      .use(ApolloInstances.ORGANIZATION)
      .watchQuery<{ activeOrgs: Array<ActiveOrganization> }>({
        query: ACTIVE_ORGS,
        variables: { orgType: isSeller ? 'SELLERS' : 'PROVIDERS' },
      })
      .valueChanges.pipe(
        take(1),
        map((request) => request.data.activeOrgs),
      );
  }

  getAllOrganizationProviders(): Observable<Array<Partial<Organization>>> {
    return this.apollo
      .use(ApolloInstances.ORGANIZATION)
      .watchQuery<{ organizations: Array<Partial<Organization>> }>({
        query: GET_ALL_ORGANIZATION_PROVIDERS,
      })
      .valueChanges.pipe(
        take(1),
        map((request) => request.data.organizations),
      );
  }

  addOrganizationsToHierarchy(
    parentOrgId: number,
    children: Array<{ childOrgId: number; date: string }>,
  ) {
    return this.apollo
      .use(ApolloInstances.ORGANIZATION)
      .mutate<{ addMembers: Organization }>({
        variables: {
          addMembersRequest: {
            parentOrgId,
            children,
          },
        },
        mutation: ADD_ORGANIZATIONS_INTO_HIERARCHY,
      });
  }

  removeOrganizationsFromHierarchy(
    parentOrgId: number,
    children: Array<{ childOrgId: number; date: string }>,
  ) {
    return this.apollo
      .use(ApolloInstances.ORGANIZATION)
      .mutate<{ removeMembers: Organization }>({
        variables: {
          removeMembersRequest: {
            parentOrgId,
            children,
          },
        },
        mutation: REMOVE_ORGANIZATIONS_FROM_HIERARCHY,
      });
  }
}
